import { Button } from "@studyplus/boron-ui";
import { useEffect, useState } from "react";
import TagButton from "../../../../../components/features/TagButton";
import { Text } from "../../../../../components/general/Text";
import { useFetchAllTags } from "../../../../../hooks/http/useFetchTags";
import { useFlashMessage } from "../../../../../hooks/useFlashMessage";
import SectionInterface from "../../../../../interfaces/SectionInterface";
import { StudentPasscodeInactivationTagModal } from "../StudentPasscodeInactivationTagModal";
import { useTagSelection } from "../StudentPasscodeInactivationTagModal/useTagSelection";
import styles from "../styles.scss";
import { NO_STUDENT_ERROR } from "./downloadCsvForStudentPasscodeInactivation";
import { useFetchCsvForStudentPasscodeInactivation } from "./useFetchCsvForStudentPasscodeInactivation";

export const TAG_TYPES = ["status", "job", "tag_ids"] as const;
export type TagType = (typeof TAG_TYPES)[number];
export type Tag = {
  id: string;
  name: string;
  type: TagType;
  enabled: boolean;
  deleted: boolean;
  studentCount?: number;
};

export const DownloadForm = ({ section }: { section: SectionInterface }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<string | undefined>();
  const { showErrorMessage } = useFlashMessage();
  const { data: tags } = useFetchAllTags({
    sectionId: section.id,
    params: { types: TAG_TYPES },
  });
  const filteredTags = tags?.filter((tag): tag is Tag => {
    const validTypes: readonly string[] = TAG_TYPES;
    return validTypes.includes(tag.type);
  });

  const {
    selectedTags,
    tagSelectionError,
    applySelectedTags,
    removeTag,
    validateTagSelection,
  } = useTagSelection(filteredTags);

  const { downloadStudentsCsv, error } =
    useFetchCsvForStudentPasscodeInactivation(section.id, selectedTags);

  useEffect(() => {
    if (!error) {
      return setDownloadError(undefined);
    }
    console.error(error);

    if (error.message === NO_STUDENT_ERROR) {
      return setDownloadError(NO_STUDENT_ERROR);
    }

    showErrorMessage("ダウンロードできませんでした");
  }, [error, showErrorMessage]);

  const handleDownloadCsv = () => {
    setDownloadError(undefined);
    if (!validateTagSelection()) {
      return;
    }
    downloadStudentsCsv();
  };

  return (
    <div className={styles.box}>
      <div className={styles.boxHeading}>
        <Text className={styles.boxHeadingTitle}>
          1. 停止する生徒をタグで選択し、CSVをダウンロード
        </Text>
        <Text className={styles.notice}>
          ※招待中・連携中の生徒のみCSVに含まれます
        </Text>
      </div>
      <div
        className={`flex items-center mb-3 gap-5 ${tagSelectionError || downloadError ? styles.hasError : ""}`}
      >
        <TagButton
          colorType="primary"
          variant="solid"
          label="選択"
          iconName="icon-plus-tag"
          onClick={() => setIsModalOpen(true)}
        />
        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {selectedTags.map((tag) => (
              <TagButton
                key={tag.id}
                label={tag.name}
                iconName="icon-close-x"
                variant="outline"
                colorType="primary"
                onClick={() => removeTag(tag.id)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="mt-7">
        <Button
          variant="outline"
          size="sm"
          isRound={true}
          onClick={handleDownloadCsv}
        >
          CSVダウンロード
        </Button>
        {tagSelectionError && (
          <div className={styles.errorMessage}>{tagSelectionError}</div>
        )}
        {downloadError && (
          <div className={styles.errorMessage}>{downloadError}</div>
        )}
      </div>
      <StudentPasscodeInactivationTagModal
        sectionId={section.id}
        tags={filteredTags || []}
        selectedTags={selectedTags}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={applySelectedTags}
      />
    </div>
  );
};
